import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Users, MapPin, Search, Globe } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

import { API } from "@/config";
const FALLBACK_AVATAR =
  "https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png";

// Featured national leaders
const FEATURED_LEADERS = [
  {
    id: "balen-shah",
    name: "Balendra Shah (Balen)",
    party: "Rastriya Swatantra Party",
    role: "PM Candidate (2082)",
    constituency: "Jhapa-5 (2026)",
    image_url: "https://upload.wikimedia.org/wikipedia/commons/4/4a/Balen_Shah.jpg",
  },
  {
    id: "gagan-kumar-thapa",
    name: "Gagan Kumar Thapa",
    party: "Nepali Congress",
    role: "Party President, PM Candidate",
    constituency: "Sarlahi-4 (2026)",
    image_url: "https://upload.wikimedia.org/wikipedia/commons/7/7a/Gagan_Thapa_2022.jpg",
  },
  {
    id: "kp-sharma-oli",
    name: "K.P. Sharma Oli",
    party: "CPN-UML",
    role: "Former Prime Minister",
    constituency: "Jhapa (Federal)",
    image_url: "https://upload.wikimedia.org/wikipedia/commons/e/e2/K.P._Sharma_Oli.jpg",
  },
  {
    id: "prachanda",
    name: "Pushpa Kamal Dahal (Prachanda)",
    party: "Nepali Communist Party (NCP)",
    role: "NCP Leader, Former PM",
    constituency: "Eastern Rukum-1 (2026)",
    image_url: "https://upload.wikimedia.org/wikipedia/commons/3/3a/Pushpa_Kamal_Dahal_2015-01-06.jpg",
  },
  {
    id: "rabi-lamichhane",
    name: "Rabi Lamichhane",
    party: "Rastriya Swatantra Party",
    role: "Party President",
    constituency: "Chitwan-2 (Federal, 2079)",
    image_url: "https://upload.wikimedia.org/wikipedia/commons/2/2d/Rabi_Lamichhane_2019.jpg",
  },
];

const NATIONAL_PARTIES_2026 = [
  {
    id: "nepali-congress",
    name: "Nepali Congress",
    founded: "1950",
    intro: "Centrist to center-left democratic party with a long role in Nepal's parliamentary politics.",
    rise: "Maintained national-party status by winning FPTP seats and clearing the PR vote threshold.",
    wiki: "https://en.wikipedia.org/wiki/Nepali_Congress",
  },
  {
    id: "cpn-uml",
    name: "CPN-UML",
    founded: "1991",
    intro: "Left-wing party rooted in Marxist–Leninist ideology with a nationwide organization.",
    rise: "Sustained national-party status through strong PR vote share and FPTP wins.",
    wiki: "https://en.wikipedia.org/wiki/Communist_Party_of_Nepal_(Unified_Marxist%E2%80%93Leninist)",
  },
  {
    id: "rsp",
    name: "Rastriya Swatantra Party",
    founded: "2022",
    intro: "Centrist reform-oriented party emphasizing governance, anti-corruption, and service delivery.",
    rise: "Rapid national rise and parliamentary representation helped it secure national-party status.",
    wiki: "https://en.wikipedia.org/wiki/Rastriya_Swatantra_Party",
  },
  {
    id: "rpp",
    name: "Rastriya Prajatantra Party",
    founded: "1990",
    intro: "Center-right party known for monarchist and conservative platforms.",
    rise: "Maintained national-party status with continued parliamentary presence.",
    wiki: "https://en.wikipedia.org/wiki/Rastriya_Prajatantra_Party",
  },
  {
    id: "cpn-maoist",
    name: "CPN (Maoist Centre)",
    founded: "1994",
    intro: "Left-wing party with roots in the Maoist insurgency, now a mainstream political force.",
    rise: "Key coalition partner in multiple governments since 2008.",
    wiki: "https://en.wikipedia.org/wiki/Communist_Party_of_Nepal_(Maoist_Centre)",
  },
  {
    id: "janamat",
    name: "Janamat Party",
    founded: "2019",
    intro: "Centre-left party with a strong regional base and social-democratic messaging.",
    rise: "Expanded its electoral footprint to qualify for national-party recognition.",
    wiki: "https://en.wikipedia.org/wiki/Janamat_Party",
  },
];

const OTHER_FEDERAL_PARTIES_2026 = [
  {
    id: "nmkp",
    name: "Nepal Majdoor Kisan Party",
    founded: "1975",
    intro: "Far-left party with a long-standing base in local politics.",
    rise: "Holds FPTP representation, keeping a presence in the federal parliament.",
    wiki: "https://en.wikipedia.org/wiki/Nepal_Workers_and_Peasants_Party",
  },
  {
    id: "janamorcha",
    name: "Rastriya Janamorcha",
    founded: "2006",
    intro: "Far-left party with an anti-federalist and Marxist–Leninist orientation.",
    rise: "Maintains representation through direct seats despite lower PR share.",
    wiki: "https://en.wikipedia.org/wiki/Rastriya_Janamorcha",
  },
  {
    id: "jsp",
    name: "Janata Samajwadi Party",
    founded: "2020",
    intro: "Centre-left party focused on Madheshi rights and social justice.",
    rise: "Formed through merger, maintains federal parliamentary presence.",
    wiki: "https://en.wikipedia.org/wiki/Janata_Samajwadi_Party,_Nepal",
  },
];

const CandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [featuredLeaders] = useState(FEATURED_LEADERS);
  const [nationalParties] = useState(NATIONAL_PARTIES_2026);
  const [otherFederalParties] = useState(OTHER_FEDERAL_PARTIES_2026);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("featured");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/candidates`);
        const apiCandidates = Array.isArray(res.data) ? res.data : [];
        setCandidates(apiCandidates);
      } catch (e) {
        toast.error("Failed to load simulation candidates");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredFeatured = featuredLeaders.filter(
    (c) =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.party.toLowerCase().includes(search.toLowerCase()) ||
      c.constituency.toLowerCase().includes(search.toLowerCase())
  );

  const filteredSimCandidates = (candidates || []).filter(
    (c) =>
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.party || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-peace-blue to-[#001529]">
      {/* Ticker */}
      <div className="bg-gradient-to-r from-gorkhali-red via-peace-blue to-gorkhali-red py-3 overflow-hidden">
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: [0, -1800] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        >
          {[...Array(4)].map((_, i) => (
            <span
              key={i}
              className="mx-8 text-white font-bebas tracking-wider text-sm"
            >
              🧑‍💼 CANDIDATES • DEUBA • OLI • PRACHANDA • BALEN • RABI • ALL MAJOR PARTIES •{" "}
              <span className="text-marigold">★</span>
            </span>
          ))}
        </motion.div>
      </div>

      {/* Hero */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-gorkhali-red/20 rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-10 right-10 w-72 h-72 bg-rsp-blue/20 rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: "1s" }}
          />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }}>
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="inline-block mb-6"
            >
              <Users className="mx-auto text-marigold" size={64} />
            </motion.div>
            <h1
              className="font-bebas text-5xl sm:text-6xl text-white tracking-wider mb-4"
              style={{ textShadow: "0 0 60px rgba(247,127,0,0.5)" }}
            >
              CANDIDATES
            </h1>
            <p className="font-playfair text-xl text-gray-300 italic mb-2">
              "From veteran leaders to a new generation of reformers"
            </p>
            <p className="text-sm text-gray-400 max-w-2xl mx-auto">
              Top national leaders and simulation candidates for the Prime Minister election game.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Search + tabs */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <Input
                placeholder="Search by name, party, or constituency..."
                className="pl-9 bg-white/5 border-white/10 text-sm text-white placeholder:text-gray-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="candidates-search"
              />
            </div>

            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full md:w-auto"
            >
              <TabsList className="grid grid-cols-2 w-full md:w-64">
                <TabsTrigger value="featured" className="font-bebas tracking-wider" data-testid="tab-major-leaders">
                  MAJOR LEADERS
                </TabsTrigger>
                <TabsTrigger value="simulation" className="font-bebas tracking-wider" data-testid="tab-game-players">
                  GAME PLAYERS
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsContent value="featured" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredFeatured.map((c) => (
                  <motion.div
                    key={c.id}
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="bg-white/5 backdrop-blur border border-white/10 rounded-lg p-4 flex gap-4"
                    data-testid={`featured-${c.id}`}
                  >
                    <img
                      src={c.image_url}
                      alt={c.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-marigold"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK_AVATAR;
                      }}
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <h3 className="font-bebas text-lg text-white tracking-wider truncate">
                        {c.name}
                      </h3>
                      <p className="text-sm text-marigold truncate">{c.party}</p>
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{c.role}</p>
                      <p className="text-xs text-gray-300 mt-2 flex items-center gap-1">
                        <MapPin size={12} className="text-gorkhali-red" />
                        {c.constituency}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="simulation" className="mt-4">
              {loading ? (
                <div className="flex justify-center py-16">
                  <motion.div
                    className="w-10 h-10 border-4 border-marigold border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  />
                </div>
              ) : filteredSimCandidates.length ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredSimCandidates.map((c) => (
                    <motion.div
                      key={c.id}
                      whileHover={{ y: -4, scale: 1.02 }}
                      className="bg-white/5 backdrop-blur border border-white/10 rounded-lg p-4 flex gap-4"
                      data-testid={`sim-candidate-${c.id}`}
                    >
                      <img
                        src={c.image_url || FALLBACK_AVATAR}
                        alt={c.name}
                        className="w-16 h-16 rounded-full object-cover border-2"
                        style={{ borderColor: c.party_color || "#F77F00" }}
                        onError={(event) => {
                          event.currentTarget.src = FALLBACK_AVATAR;
                        }}
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="font-bebas text-lg text-white tracking-wider truncate">
                          {c.name}
                        </h3>
                        <p
                          className="text-sm truncate"
                          style={{ color: c.party_color || "#F77F00" }}
                        >
                          {c.party}
                        </p>
                        {c.slogan && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">
                            "{c.slogan}"
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center text-gray-400 text-sm font-inter">
                  No simulation candidates loaded.
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Official PR list callout */}
          <div className="mt-10 max-w-3xl mx-auto bg-white/5 border border-white/10 rounded-lg p-5">
            <h3 className="font-bebas text-lg text-white tracking-wider mb-2 flex items-center gap-2">
              <Globe size={18} className="text-marigold" />
              OFFICIAL PR CANDIDATE LIST (2082)
            </h3>
            <p className="font-inter text-xs text-gray-300 mb-2">
              For the full proportional-representation (PR) candidate list published by
              the Election Commission of Nepal, open the official PDF below.
            </p>
            <a
              href="https://election.gov.np/admin/public//storage/HOR%202082/PR/PreliminaryCandidateListPR.pdf"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-xs text-marigold hover:text-white font-inter mt-1"
              data-testid="official-pr-link"
            >
              View official PR candidate PDF →
            </a>
          </div>

          {/* Political parties 2026 */}
          <div className="mt-12">
            <h3 className="font-bebas text-2xl text-white tracking-wider mb-2">
              POLITICAL PARTIES IN NEPAL (2026)
            </h3>
            <p className="text-sm text-gray-400 max-w-3xl">
              Brief introductions for parties with national or federal parliamentary representation.
            </p>

            <div className="mt-6">
              <h4 className="font-bebas text-lg text-marigold tracking-wider mb-3">
                National Parties
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {nationalParties.map((party) => (
                  <div
                    key={party.id}
                    className="bg-white/5 border border-white/10 rounded-lg p-4"
                    data-testid={`party-${party.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h5 className="font-bebas text-lg text-white tracking-wider">
                        {party.name}
                      </h5>
                      <span className="text-xs text-gray-400">{party.founded}</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-2">{party.intro}</p>
                    <p className="text-xs text-gray-400 mt-2">{party.rise}</p>
                    <a
                      href={party.wiki}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-marigold hover:text-white mt-3"
                    >
                      Wikipedia →
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <h4 className="font-bebas text-lg text-marigold tracking-wider mb-3">
                Other Parties in Federal Parliament
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {otherFederalParties.map((party) => (
                  <div
                    key={party.id}
                    className="bg-white/5 border border-white/10 rounded-lg p-4"
                    data-testid={`party-${party.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h5 className="font-bebas text-lg text-white tracking-wider">
                        {party.name}
                      </h5>
                      <span className="text-xs text-gray-400">{party.founded}</span>
                    </div>
                    <p className="text-xs text-gray-300 mt-2">{party.intro}</p>
                    <p className="text-xs text-gray-400 mt-2">{party.rise}</p>
                    <a
                      href={party.wiki}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-marigold hover:text-white mt-3"
                    >
                      Wikipedia →
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CandidatesPage;
